/** @format */

import {useCallback, useMemo, useRef, useState} from 'react';

import {KeyboardArrowDown} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';

import {useCallbackOnReturn} from 'helpers/routerHelpers';

import TertiarySelect from 'components/selects/TertiarySelect';

import {YalperAnalyticAttributionModelEnum} from 'graphql/generated/types';

import {ATTRIBUTION_MODELS} from 'pages/Home/Project/Steps/Periods/components/KpiPlanForm/constants';

import GoalPicker, {GoalPickerGoal} from 'pages/Home/Project/components/GoalPicker';
import {PickerLeft, PickerRight, PickerRow} from 'pages/Home/Project/components/GoalPicker/styles';

import {
  useGetAnalyticsSettingsQuery,
  useSaveAnalyticsKpiSettingsMutation,
} from './graphql/__generated__';
import {
  ConfirmDialog,
  FieldLabel,
  FieldWrapper,
  FieldsBlock,
  GoalPickerButton,
  InfoCard,
  InfoCardContent,
  InfoCardIcon,
  InfoCardList,
  InfoCardText,
  InfoCardTitle,
  SettingsContainer,
} from './styles';

const DEFAULT_REVENUE_GOAL_LABEL = 'Ecommerce: покупка';

type PendingChange =
  | {type: 'attributionModel'; value: YalperAnalyticAttributionModelEnum}
  | {type: 'revenueGoalId'; value: number | null};

interface AnalyticsSettingsProps {
  companyId: string;
}

const AnalyticsSettings = ({companyId}: AnalyticsSettingsProps) => {
  const {t} = useTranslation();

  const {data, loading, error, refetch} = useGetAnalyticsSettingsQuery({
    variables: {companyId},
  });

  useCallbackOnReturn(refetch);

  const [saveSettings, {loading: saving}] = useSaveAnalyticsKpiSettingsMutation({
    refetchQueries: ['GetAnalyticsSettings'],
  });

  const [pendingChange, setPendingChange] = useState<PendingChange | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerAnchorRef = useRef<HTMLButtonElement | null>(null);

  const kpiSettings = data?.currentUser?.company?.kpiSettings;
  const analyticGoals = data?.currentUser?.company?.analyticGoals ?? [];

  const currentAttributionModel: YalperAnalyticAttributionModelEnum =
    kpiSettings?.attributionModel ?? 'AUTOMATIC';
  const currentRevenueGoalId = kpiSettings?.revenueGoalId ?? null;
  const currentGoals = useMemo(() => kpiSettings?.goals ?? [], [kpiSettings?.goals]);

  const attributionModelOptions = useMemo(
    () =>
      ATTRIBUTION_MODELS.map(model => ({
        value: model.value,
        text: model.label,
      })),
    [],
  );

  const revenueGoalDisplayValue = useMemo(() => {
    if (currentRevenueGoalId === null) {
      return DEFAULT_REVENUE_GOAL_LABEL;
    }

    const goal = analyticGoals.find(g => g.id === currentRevenueGoalId);
    if (goal) {
      return goal.name;
    }

    return `ID: ${currentRevenueGoalId} (${t('цель не найдена', 'цель не найдена')})`;
  }, [currentRevenueGoalId, analyticGoals, t]);

  const handleAttributionModelChange = useCallback(
    (value: YalperAnalyticAttributionModelEnum) => {
      if (value === currentAttributionModel) return;
      setPendingChange({type: 'attributionModel', value});
      setDialogOpen(true);
    },
    [currentAttributionModel],
  );

  const handleRevenueGoalChange = useCallback(
    (value: number | null) => {
      if (value === currentRevenueGoalId) return;
      setPendingChange({type: 'revenueGoalId', value});
      setDialogOpen(true);
    },
    [currentRevenueGoalId],
  );

  const handleGoalPickerSelect = (goal: GoalPickerGoal) => {
    setPickerOpen(false);
    handleRevenueGoalChange(goal.id);
  };

  const handleDefaultGoalSelect = () => {
    setPickerOpen(false);
    handleRevenueGoalChange(null);
  };

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setPendingChange(null);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!pendingChange) return;

    const settings =
      pendingChange.type === 'attributionModel'
        ? {
            attributionModel: pendingChange.value,
            goals: [...currentGoals],
            revenueGoalId: currentRevenueGoalId,
          }
        : {
            attributionModel: currentAttributionModel,
            goals: [...currentGoals],
            revenueGoalId: pendingChange.value,
          };

    try {
      await saveSettings({
        variables: {
          companyId,
          settings,
        },
      });
      setDialogOpen(false);
      setPendingChange(null);
    } catch {
      setDialogOpen(false);
      setPendingChange(null);
    }
  }, [
    pendingChange,
    companyId,
    currentAttributionModel,
    currentGoals,
    currentRevenueGoalId,
    saveSettings,
  ]);

  const hasGoals = analyticGoals.length > 0;
  const isDisabled = loading || saving;
  const isGoalPickerDisabled = isDisabled || !hasGoals || !!error;

  return (
    <SettingsContainer>
      <FieldsBlock>
        <FieldWrapper>
          <FieldLabel>
            <Trans>Цель для сбора дохода</Trans>
          </FieldLabel>
          <GoalPickerButton
            ref={pickerAnchorRef}
            onClick={() => setPickerOpen(true)}
            disabled={isGoalPickerDisabled}
            aria-label={t('Цель для сбора дохода', 'Цель для сбора дохода')}
          >
            <Box>{revenueGoalDisplayValue}</Box>
            <KeyboardArrowDown sx={{opacity: 0.6}} />
          </GoalPickerButton>
          <GoalPicker
            open={pickerOpen}
            anchorEl={pickerAnchorRef.current}
            goals={analyticGoals as GoalPickerGoal[]}
            onSelect={handleGoalPickerSelect}
            onClose={() => setPickerOpen(false)}
            headerSlot={
              <PickerRow onClick={handleDefaultGoalSelect}>
                <PickerLeft>
                  <Box sx={{fontWeight: 400, fontSize: '14px'}}>—</Box>
                </PickerLeft>
                <PickerRight>
                  <Box sx={{fontWeight: 400, fontSize: '14px'}}>{DEFAULT_REVENUE_GOAL_LABEL}</Box>
                  <Box sx={{opacity: 0.7, fontSize: '14px'}}>
                    <Trans>По умолчанию</Trans>
                  </Box>
                </PickerRight>
              </PickerRow>
            }
          />
        </FieldWrapper>

        <FieldWrapper>
          <FieldLabel>
            <Trans>Модель атрибуции</Trans>
          </FieldLabel>
          <TertiarySelect
            value={currentAttributionModel}
            options={attributionModelOptions}
            onSelect={handleAttributionModelChange}
            disabled={isDisabled}
            aria-label={t('Модель атрибуции', 'Модель атрибуции')}
          />
        </FieldWrapper>
      </FieldsBlock>

      <InfoCard>
        <InfoCardContent>
          <InfoCardTitle>
            <Trans>Сбор дохода</Trans>
          </InfoCardTitle>
          <InfoCardText>
            <Trans>
              Доход учитывается по выбранной цели из Яндекс Метрики и распределяется между
              источниками по модели атрибуции
            </Trans>
          </InfoCardText>
          <InfoCardList>
            <Trans>Модель атрибуции также используется:</Trans>
            <ul>
              <li>
                <Trans>для расчета CPL и Конверсий, когда они выбраны в качестве KPI</Trans>
              </li>
              <li>
                <Trans>для расчета воронки</Trans>
              </li>
            </ul>
          </InfoCardList>
          <InfoCardText>
            <Trans>
              По умолчанию используется цель «Ecommerce: покупка» и модель «Автоматическая»
            </Trans>
          </InfoCardText>
        </InfoCardContent>
        <InfoCardIcon component='span' className='material-symbols-outlined'>
          payment_arrow_down
        </InfoCardIcon>
      </InfoCard>

      <ConfirmDialog
        open={dialogOpen}
        onClose={saving ? undefined : handleDialogClose}
        disableEscapeKeyDown={saving}
      >
        <DialogTitle>
          <Trans>Подтверждение изменения</Trans>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Trans>
              Вы уверены, что хотите изменить настройки аналитики? Это может повлиять на расчёт
              показателей.
            </Trans>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={saving} color='inherit'>
            <Trans>Отмена</Trans>
          </Button>
          <Button onClick={handleConfirm} disabled={saving} variant='contained' color='primary'>
            {saving ? <CircularProgress size={20} /> : <Trans>Подтвердить</Trans>}
          </Button>
        </DialogActions>
      </ConfirmDialog>
    </SettingsContainer>
  );
};

export default AnalyticsSettings;
